import React, { FC } from 'react'
import first from 'lodash/first'
import { Flex, HighlightedText, Button, Icon } from '@ui'
import css from './bookingRecurrence.module.scss'
import { IItemRendererProps, Select } from '@blueprintjs/select'
import { RangePattern, RecurrenceT, TypePattern } from '@providers'
import { DateUtil } from '@utils/dateUtil'
import { capitalize } from 'lodash/fp'
import { MenuItem, NumericInput } from '@blueprintjs/core'
import PopupCalendar from 'components/PopupCalendar/PopupCalendar'
import { weekdays, Weekdays } from '@utils'

type BookingCalendarPropsT = {
  recurrence: RecurrenceT
  setRecurredBooking: (param: Partial<RecurrenceT>, fromDate: DateUtil) => void
  selected: DateUtil[]
  timezone: string
  constraints: { first: DateUtil; last: DateUtil }
}

const BookingRecurrence: FC<BookingCalendarPropsT> = ({
  recurrence,
  setRecurredBooking,
  selected,
  constraints,
}) => {
  const fromDate = first(selected) as DateUtil
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const typeItems: any = Object.entries(TypePattern).reduce(
    (a, [k, v]) => ({
      ...a,
      [capitalize(v)]: v,
    }),
    {}
  )
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const rangeItems: { code: any; values: any } = Object.entries(RangePattern).reduce(
    (a, [k, v]) => ({
      ...a,
      code: { ...a.code, [capitalize(k.replace('_', ' '))]: v },
      values: { ...a.values, [v]: capitalize(k.replace('_', ' ')) },
    }),
    { values: {}, code: {} }
  )
  const isNoRecurrence = recurrence.typePattern === TypePattern.NEVER
  const isWeeklyRecurrence = recurrence.typePattern === TypePattern.WEEKLY
  const isMonthlyRecurrence = recurrence.typePattern === TypePattern.MONTHLY
  const isEndDate = recurrence.rangePattern === RangePattern.ON_DATE
  const typeValue = capitalize(recurrence.typePattern.toString() || '')
  const rangeValue = rangeItems.values[recurrence.rangePattern || '']
  const nInputStyle = { width: 156 }
  const minMaxValues = { min: 1, max: isWeeklyRecurrence ? 10 : isMonthlyRecurrence ? 12 : 31 }
  const onClickHandler = (s: string) => {
    const isActive = recurrence.daysOfWeek.includes(s as Weekdays)
    const daysOfWeek = (
      isActive ? recurrence.daysOfWeek.filter((e) => e !== s) : [...recurrence.daysOfWeek, s]
    ) as Weekdays[]
    setRecurredBooking({ daysOfWeek }, fromDate)
  }

  return (
    <div className={css.wrapper}>
      <Flex align="baseline" direction="column" className={css.info_block}>
        <Flex className="mb-4">
          <div className={css.typePattern}>
            <div className="mb-2">Repeat</div>
            <Select
              items={Object.keys(typeItems)}
              scrollToActiveItem
              activeItem={typeValue}
              onItemSelect={(e) => {
                const typePattern = typeItems[e]
                setRecurredBooking({ typePattern }, fromDate)
              }}
              itemRenderer={MomentRenderer}>
              <Button className={css.inputBox} text={typeValue}>
                <span>
                  <i className={`fa-light fa-angle-down ${css.selector}`}></i>
                </span>
              </Button>
            </Select>
          </div>
          {!isNoRecurrence && (
            <div>
              <div className="mb-2">
                Every # {isMonthlyRecurrence ? 'Month' : isWeeklyRecurrence ? 'Week' : 'Day'}(s){' '}
              </div>
              <Flex>
                <NumericInput
                  minorStepSize={null}
                  className="mr-4"
                  style={nInputStyle}
                  buttonPosition="none"
                  value={recurrence.typeInterval}
                  onValueChange={(typeInterval) => {
                    setRecurredBooking({ typeInterval }, fromDate)
                  }}
                  {...minMaxValues}
                />
                {isWeeklyRecurrence && (
                  <div>
                    {weekdays.map((d) => (
                      <div
                        className={`${css.weekDays} ${
                          recurrence.daysOfWeek.includes(d) ? css.selected : ''
                        }`}
                        onClick={() => onClickHandler(d)}>
                        {d.charAt(0)}
                      </div>
                    ))}
                  </div>
                )}
                {isMonthlyRecurrence && <div>On day {fromDate?.date()}</div>}
              </Flex>
            </div>
          )}
        </Flex>
        {!isNoRecurrence && (
          <Flex>
            <div>
              <div className="mb-2">End</div>
              <Select
                items={Object.keys(rangeItems.code)}
                scrollToActiveItem
                activeItem={rangeValue}
                onItemSelect={(e) => {
                  const rangePattern = rangeItems.code[e]
                  setRecurredBooking({ rangePattern }, fromDate)
                }}
                itemRenderer={MomentRenderer}>
                <Button className={css.inputBox} text={rangeValue} />
              </Select>
            </div>
            <div>
              <div className="mb-2">{isEndDate ? 'Select End Date' : 'Occurence(s)'}</div>
              <Flex>
                {isEndDate ? (
                  <PopupCalendar
                    value={recurrence.rangeDate.format('MM-dd-yy')}
                    className={css.inputBox}
                    selectedDates={[recurrence.rangeDate]}
                    onDateSelect={(dates) => {
                      setRecurredBooking({ rangeDate: dates[0] }, fromDate)
                    }}
                    constraints={constraints}
                  />
                ) : (
                  <NumericInput
                    minorStepSize={null}
                    buttonPosition="none"
                    style={nInputStyle}
                    value={recurrence.rangeInterval}
                    onValueChange={(rangeInterval) => {
                      setRecurredBooking({ rangeInterval }, fromDate)
                    }}
                    min={1}
                    max={recurrence.numberLimit}
                  />
                )}
              </Flex>
            </div>
          </Flex>
        )}
      </Flex>
    </div>
  )
}

function MomentRenderer(item: string, { handleClick, modifiers, query }: IItemRendererProps) {
  return (
    <MenuItem
      key={item}
      active={modifiers.active}
      disabled={modifiers.disabled}
      onClick={handleClick}
      text={
        <div className="flex items-center">
          <HighlightedText text={item} query={query} />
        </div>
      }
    />
  )
}

export default BookingRecurrence
